import React, { useRef, useEffect, useState } from 'react'
import Timeline, {
    TimelineHeaders,
    DateHeader,
    ReactCalendarItemRendererProps,
    Unit as DateUnit
} from 'react-calendar-timeline'
import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'
import Xarrow from 'react-xarrows'
import DateService from 'live/services/date'
import containerResizeDetector from 'react-calendar-timeline/lib/resize-detector/container'

import moment, { Moment } from 'moment'

import 'react-calendar-timeline/lib/Timeline.css'
import LiveSelect from 'live/components/forms/ui/select'
import * as S from './styled'
import MouseTooltip from './mouseFollow'

const COLOR_BY_LEVELS: any = ['#008AC7', '#01C292', '#D9534F', '#F89300', '#F9E47F']

enum OPERATIONS {
    'intervention',
    'section',
    'run',
    'activity',
    'subActivity'
}

const FORMAT_STRINGS: { [key: string]: string } = {
    minute: 'mm',
    hour: 'HH',
    day: 'DD',
    month: 'MMMM, YYYY',
    year: 'YYYY',
    week: 'w - MMMM, YYYY'
}

const FORMAT_STRINGS_PARENT: { [key: string]: string } = {
    minute: 'mm',
    hour: 'HH:00, DD, MMMM, YYYY',
    day: 'DD, MMMM, YYYY',
    month: 'MMMM, YYYY',
    year: 'YYYY',
    week: 'MMMM, YYYY'
}

const zoomLevels = ['6months', '2months', 'month', 'week', 'day', 'hour', 'minute']
const zoomLevelsLabels = ['Semester', '2 Months', 'Month', 'Week', 'Days', 'Hours', 'Minutes']
//const zoomLevels = ['year', '6months', '2months', 'month', 'day', 'hour', 'minute']
export interface GanttItem {
    id: number | string
    start_time: Moment | string
    end_time: Moment | string
    group: number | string
    layerDepth: number
    realized?: boolean
    executed?: boolean
    execution?: boolean
    planned?: boolean
    predecessorsIdsList: any
}
export interface GanttGroup {
    id: number | string
    title: string
    rightTitle: JSX.Element
    derrick: string
}

interface View {
    value: string
    label: string
}
interface Props {
    headerUnit?: DateUnit
    view: string
    selectedIndex?: number
    items: GanttItem[]
    groups: any
    setSelectedIndex?: (index: number) => void
    setSelectedNodeId?: (index: number) => void
    scrollRef?: (element: any) => void
    lineHeight?: number
    lineItemHeight?: number
    viewModeValue?: View
    viewModeOptions?: View[]
    viewModeOnChange?: (value: any, option: any) => void
    openModalWithRelation?: (selected: GanttItem) => void
    saveOperationChanges?: (values: any, nodeId: number | string) => void
    hideHeader?: boolean
    editable?: any
    setEditable?: any
    visibleStartTime?: any
    visibleEndTime?: any
    setVisibleStartTime: (time: any) => void
    setVisibleEndTime: (time: any) => void
    zoom: number
    setZoom: (zoom: number) => void
    setLinkX: any
    setLinkY: any
}

const GanttChart: React.FC<Props> = ({
    items = [],
    groups = [],
    view,
    headerUnit = 'month',
    scrollRef,
    setSelectedIndex,
    setSelectedNodeId,
    selectedIndex,
    lineHeight,
    lineItemHeight,
    viewModeValue,
    viewModeOptions,
    viewModeOnChange,
    openModalWithRelation,
    saveOperationChanges,
    hideHeader,
    editable,
    setEditable,
    visibleStartTime,
    visibleEndTime,
    setVisibleStartTime,
    setVisibleEndTime,
    zoom,
    setZoom,
    setLinkX,
    setLinkY
}) => {
    const [linkButtonState, setLinkButtonState] = React.useState<any>()
    const linkButtonRef = useRef()
    const posX = useRef(0)
    const posY = useRef(0)

    const lastStart = useRef(0)

    const [unit, setUnit] = React.useState<DateUnit>(headerUnit)
    const [divisorsUnit, setDivisorsUnit] = React.useState<DateUnit>(headerUnit)
    if (!editable && !setEditable) {
        const [editable, setEditable] = React.useState<boolean>(false)
    }
    const [timelineRef, setTimelineRef] = React.useState<any>()
    const [selectedStartTime, setSelectedStartTime] = React.useState<any>(
        items.length > 0 ? items[0].start_time : undefined
    )
    const [selectedEndTime, setSelectedEndTime] = React.useState<any>(items.length > 0 ? items[0].end_time : undefined)
    const [originalStartTime, setOriginalStartTime] = React.useState<any>(undefined)
    const [originalEndTime, setOriginalEndTime] = React.useState<any>(undefined)
    const [ganttItems, setGanttItems] = React.useState<GanttItems[]>(items)
    const [selectedItem, setSelectedItem] = React.useState<GanttItems>()
    const [mouseFollow, setMouseFollow] = React.useState<boolean>(false)
    const [viewModeValueIcon, setViewModeValueIcon] = React.useState<any>(viewModeValue)
    const [linkId, setLinkId] = React.useState<string>()
    const [direction, setDirection] = React.useState<string>()
    const [selectedConnectionType, setSelectedConnectionType] = React.useState<string>()
    const [confirmChanges, setConfirmChanges] = React.useState<string>()
    const [showAddRelationModal, setShowAddRelationModal] = React.useState<boolean>(false)
    const [parentList, setParentList] = React.useState<string[]>([])
    const [labelWidth, setLabelWidth] = React.useState<number>(0)
    const [, setRender] = useState({})
    const forceRerender = () => setRender({})

    const [divisorComponent, setDivisorComponent] = React.useState<any>()

    const dateFormat = `${DateService.getDateFormat()} ${DateService.getTimeShort()}`

    useEffect(() => {
        if (timelineRef) {
            timelineRef.zoomIn = () => {}
            timelineRef.zoomOut = () => {}
            timelineRef.changeZoom = () => {}
            if (!zoom && zoom !== 0) {
                let newZoomRange = setZoomRange(zoomLevels[1])
                if (setVisibleStartTime) {
                    setVisibleStartTime(newZoomRange.visibleTimeStart)
                }
                if (setVisibleEndTime) {
                    setVisibleEndTime(newZoomRange.visibleTimeEnd)
                }
                setZoom(1)
                timelineRef.updateScrollCanvas(newZoomRange.visibleTimeStart, newZoomRange.visibleTimeEnd)
            } else {
                setZoomRange(zoomLevels[zoom])
                if (setVisibleStartTime) {
                    setVisibleStartTime(visibleStartTime)
                }
                if (setVisibleEndTime) {
                    setVisibleEndTime(visibleEndTime)
                }
                timelineRef.updateScrollCanvas(visibleStartTime, visibleEndTime)
            }
            if (hideHeader) {
                changeZoomLevel('minus')
            }
        }
    }, [timelineRef, visibleStartTime, visibleEndTime])

    useEffect(() => {
        if (selectedIndex && !editable && timelineRef && ganttItems) {
            const realizedIndex = selectedIndex + '-realized'
            const selectedItem = ganttItems.find(item => item.id === realizedIndex)
            if (selectedItem) {
                const dateDiff = moment.duration(
                    moment(timelineRef.state.visibleTimeStart).diff(selectedItem.start_time)
                )

                timelineRef.updateScrollCanvas(
                    moment(timelineRef.state.visibleTimeStart)
                        .subtract(dateDiff)
                        .valueOf(),
                    moment(timelineRef.state.visibleTimeEnd)
                        .subtract(dateDiff)
                        .valueOf()
                )
            }
        }
    }, [selectedIndex])

    useEffect(() => {
        if (timelineRef && zoom >= 4) {
            const startValue = timelineRef.state.visibleTimeStart.valueOf()
            let spacing = 0
            switch (zoom) {
                case 6:
                    spacing = 180000
                    break
                case 5:
                    spacing = 2420000
                    break
                case 4:
                    spacing = 12640000
                    break
            }
            if (
                lastStart.current !== 0 &&
                (startValue >= lastStart.current + spacing || startValue <= lastStart.current - spacing)
            ) {
                if (ganttItems) {
                    let newGanttItems = [...ganttItems].map((item, index) => {
                        let newItem = { ...item }
                        if (item.predecessorsIdsList || item.successorIds) {
                            newItem.check = !newItem.check
                            newItem.side = startValue >= lastStart.current + spacing ? 'right' : 'left'
                        }
                        return newItem
                    })
                    setGanttItems(newGanttItems)
                }
                lastStart.current = startValue
            }
            if (lastStart.current === 0) {
                lastStart.current = startValue
            }
        }
    }, [timelineRef ? timelineRef.state.visibleTimeStart : timelineRef])

    useEffect(() => {
        setGanttItems(items)
    }, [items])

    useEffect(() => {
        return () => {
            setLinkX && setLinkX(posX.current)
            setLinkY && setLinkY(posY.current)
        }
    }, [linkButtonState])

    const updateXYPlusButton = (buttonRef: any) => {
        if (buttonRef) {
            const dom = buttonRef.getBoundingClientRect()
            if (dom.x !== 0 && dom.y !== 0) {
                posX.current = dom.x
                posY.current = dom.y
            }
        }
    }

    useEffect(() => {
        if (!editable) {
            setMouseFollow(false)
            setLinkId(undefined)
            setDirection(undefined)
        }
        setSelectedItem(undefined)
    }, [editable])

    useEffect(() => {
        forceRerender()
    }, [selectedItem])

    useEffect(() => {
        if (!mouseFollow) {
            setLinkId(undefined)
            setDirection(undefined)
            setShowAddRelationModal(false)
        }

        // Fill parent list with ids from ganttItems
        if (selectedItem && selectedItem.parentId && mouseFollow) {
            let newParentList = [selectedItem.parentId, selectedItem.parentId + '-realized']
            let parentIndex = ganttItems.findIndex(
                item => item.id === selectedItem.parentId || item.id === selectedItem.parentId + '-realized'
            )
            let layerDepth = ganttItems[parentIndex].layerDepth
            let currentItem
            for (let i = parentIndex; i >= 0; i--) {
                currentItem = ganttItems[i]
                if (currentItem.layerDepth < layerDepth) {
                    parentIndex = i
                    layerDepth = currentItem.layerDepth
                    newParentList.push(currentItem.id)
                    newParentList.push(currentItem.id + '-realized')
                }
            }
            setParentList(newParentList)
        }
    }, [mouseFollow])

    useEffect(() => {
        if (viewModeValue) {
            setViewModeValueIcon({
                value: viewModeValue.value,
                label: <i className="fas fa-eye"></i>
            })
        }
    }, [viewModeValue])

    useEffect(() => {
        if (ganttItems) {
            let selectedItemIndex
            if (selectedItem) {
                selectedItemIndex = ganttItems.findIndex(
                    item => item.id === selectedItem.id || item.id === selectedItem.id + 'realized'
                )
            } else {
                selectedItemIndex = 0
            }
            let newGanttItems = [...ganttItems]
            newGanttItems[selectedItemIndex].check = !newGanttItems[selectedItemIndex].check
            setGanttItems(newGanttItems)
        }
    }, [linkId, mouseFollow, showAddRelationModal, selectedItem, direction, zoom])

    useEffect(() => {
        setDivisorComponent(generateVerticalDividers(labelWidth))
    }, [divisorsUnit, labelWidth])

    // const setClassNameOnLine = useCallback(
    //     group => {
    //         if (selectedIndex || true) {
    //             if (group.id === selectedIndex + 1) {
    //                 return ['selected']
    //             }
    //         }

    //         return []
    //     },
    //     [selectedIndex]
    // )

    // const getCurrentZoomLevel = () => {
    //     let zoomLevel;
    //     const { visibleTimeStart, visibleTimeEnd } = timelineRef.state;
    //     const diff = moment
    //       (visibleTimeEnd)
    //       .diff(moment(visibleTimeStart), 'days');

    //     if (diff > 365 * 9) {
    //       zoomLevel = 0;
    //     } else if (diff > 365 * 5) {
    //       zoomLevel = 1;
    //     } else if (diff > 365) {
    //       zoomLevel = 2;
    //     } else if (diff >= 300) {
    //       zoomLevel = 3;
    //     } else if (diff >= 150) {
    //       zoomLevel = 4;
    //     } else if (diff > 30) {
    //       zoomLevel = 5;
    //     } else {
    //       zoomLevel = 6;
    //     }

    //     return zoomLevel;
    // };

    const setZoomRange = (
        zoom,
        visibleTimeStart = timelineRef.state.visibleTimeStart,
        visibleTimeEnd = timelineRef.state.visibleTimeEnd
    ) => {
        let newStart, newEnd, newUnit, newDivisorsUnit
        const diff = moment(visibleTimeEnd).diff(moment(visibleTimeStart), 'days')
        const middlePoint = moment(visibleTimeEnd).subtract(diff / 2, 'day')
        switch (zoom) {
            case 'year':
                newStart = moment(middlePoint).subtract(6, 'month')
                newEnd = moment(middlePoint).add(6, 'month')
                newUnit = 'month'
                newDivisorsUnit = 'year'
                break

            case '6months':
                newStart = moment(middlePoint).subtract(3, 'month')
                newEnd = moment(middlePoint).add(3, 'month')
                newUnit = 'month'
                newDivisorsUnit = 'month'
                break

            case '2months':
                newStart = moment(middlePoint).subtract(1, 'month')
                newEnd = moment(middlePoint).add(1, 'month')
                newUnit = 'day'
                newDivisorsUnit = 'day/simple'
                break

            case 'month':
                newStart = moment(middlePoint).subtract(2, 'week')
                newEnd = moment(middlePoint).add(2, 'week')
                newUnit = 'day'
                newDivisorsUnit = 'day/simple'
                break

            case 'week':
                newStart = moment(middlePoint).subtract(3, 'day')
                newEnd = moment(middlePoint).add(4, 'day')
                newUnit = 'week'
                newDivisorsUnit = 'month'
                break

            case 'day':
                newStart = moment(middlePoint).subtract(0.5, 'day')
                newEnd = moment(middlePoint).add(0.5, 'day')
                newUnit = 'hour'
                newDivisorsUnit = 'day/simple'
                break

            case 'hour':
                newStart = moment(middlePoint).subtract(6, 'hour')
                newEnd = moment(middlePoint).add(6, 'hour')
                newUnit = 'hour'
                newDivisorsUnit = 'day/simple' //'day';
                break

            case 'minute':
                newStart = moment(middlePoint).subtract(30, 'minute')
                newEnd = moment(middlePoint).add(30, 'minute')
                newUnit = 'minute'
                newDivisorsUnit = 'day/simple'
                break

            default:
                newStart = visibleStartTime
                newEnd = visibleEndTime
                break
        }

        setUnit(newUnit)
        setDivisorsUnit(newDivisorsUnit)
        return {
            visibleTimeStart: newStart.valueOf(),
            visibleTimeEnd: newEnd.valueOf()
        }
    }

    const changeZoomLevel = type => {
        let newZoom, nextZoom
        const maxZoom = zoomLevels.length - 1
        const minZoom = 0

        if (type === 'plus') {
            nextZoom = zoom + 1
            if (nextZoom > maxZoom) {
                return
            } else {
                newZoom = nextZoom
            }
            newZoom = nextZoom > maxZoom ? maxZoom : nextZoom
        } else {
            nextZoom = zoom - 1
            if (nextZoom < minZoom) {
                return
            } else {
                newZoom = nextZoom
            }
        }

        let newZoomRange = setZoomRange(zoomLevels[newZoom])
        if (setZoom) {
            setZoom(newZoom)
        }
        if (setVisibleStartTime) {
            setVisibleStartTime(newZoomRange.visibleTimeStart)
        }
        if (setVisibleEndTime) {
            setVisibleEndTime(newZoomRange.visibleTimeEnd)
        }
        timelineRef.updateScrollCanvas(newZoomRange.visibleTimeStart, newZoomRange.visibleTimeEnd)
    }

    const arrowSvg = {
        svgElem: 'path',
        svgProps: {
            d: `M 0 0 L 0.22 0 L 1 0.5 L 0.22 1 L 0 1 L 0.7 0.5 z`
        },
        offsetForward: 1
    }

    const parentUnit = (unit: string) => {
        if (unit === 'minute') {
            return 'hour'
        }
        if (unit === 'hour') {
            return 'day'
        }
        if (unit === 'day') {
            return 'month'
        }
        if (unit === 'week') {
            return 'month'
        }
        return 'year'
    }

    const itemRenderer = ({ item, timelineContext, itemContext, getItemProps, getResizeProps }) => {
        const style: any = {
            position: 'relative',
            backgroundColor: itemContext.selected ? 'yellow' : COLOR_BY_LEVELS[item.layerDepth],
            borderLeft: itemContext.selected && item.isLeaf ? '2px solid red' : undefined,
            borderRight: itemContext.selected && item.isLeaf ? '2px solid red' : undefined,
            cursor: itemContext.selected ? 'move' : 'default',
            borderRadius: 5,
            border: 'none',
            maxHeight: lineItemHeight ? lineItemHeight : lineHeight ? lineHeight : 13,
            transform: 'translateY(3px)' //view === 'both' ? 'translateY(4px)' : 'translateY(1px)'
        }

        if (view === 'both' && !item.realized) {
            style.backgroundColor = itemContext.selected ? 'yellow' : `${COLOR_BY_LEVELS[item.layerDepth]}66`
            style.border = `2px dashed ${COLOR_BY_LEVELS[item.layerDepth]}`
            style.borderRadius = `3px`
            style.transform = 'translateY(-2px)'
        }

        const modalOnClick = (connection: string) => {
            if (connection !== 'Modal') {
                setMouseFollow(true)
                setShowAddRelationModal(false)
                setLinkId(item.id)
                // setSelectedConnectionType(connection)
                setParentList([item.parentId])
            } else if (openModalWithRelation) {
                if (direction === 'left') {
                    openModalWithRelation(item)
                } else if (direction === 'right') {
                    openModalWithRelation(item)
                }
            }
        }
        const modalSelector = (
            <>
                <div
                    onClick={() => {
                        modalOnClick('Connection')
                    }}
                >
                    <i className="fas fa-link"></i>
                    {i18n('Link to others')}
                </div>
                <div
                    onClick={() => {
                        modalOnClick('Modal')
                    }}
                >
                    <i className="fas fa-plus"></i>
                    {i18n('Add linked task')}
                </div>
            </>
        )

        const checkExistingRelation = () => {
            if (linkId) {
                const parsedLinkIdArray = linkId.split('-')
                let parsedLinkId
                if (parsedLinkIdArray.length >= 3) {
                    parsedLinkId = '-' + parsedLinkIdArray[1]
                } else {
                    parsedLinkId = parsedLinkIdArray[0]
                }
                const checkThisItemHaveRelation = item.predecessorsIdsList.hasOwnProperty(parsedLinkId)
                // const checkLinkIdHaveRelation = (
                //   relationsMock.hasOwnProperty(parsedLinkId) &&
                //   relationsMock[parsedLinkId].id === item.group
                // )
                return checkThisItemHaveRelation //|| checkLinkIdHaveRelation
            }
            return false
        }

        const validadeClick = (side: string) => {
            validateRelationCreationByGantt(
                linkId,
                item.group,
                setShowAddRelationModal,
                setDirection,
                side,
                selectedConnectionType,
                selectedItem
            )
        }

        const plusIcon = (
            <i
                id={item.id + '-button'}
                ref={(r: any) => {
                    linkButtonRef.current = r
                    setLinkButtonState(r)
                }}
                className="fas fa-plus-circle"
                style={{
                    zIndex: 2,
                    position: 'relative'
                }}
            ></i>
        )

        const addLinkButtons = (
            <>
                {/* Left */}
                {direction !== 'right' && (
                    <S.AddLinkButtonLeft
                        style={{ marginTop: showAddRelationModal ? '-40px' : undefined }}
                        onClick={() => {
                            validadeClick('left')
                        }}
                    >
                        {plusIcon}
                    </S.AddLinkButtonLeft>
                )}

                {/* Right */}
                {/* {direction !== 'left' && (
            <S.AddLinkButtonRight
              style={{marginTop: showAddRelationModal || linkId ? '0px' : undefined}}
              onClick={() => {validadeClick('right')}} 
            >
              {plusIcon}
            </S.AddLinkButtonRight>
          )} */}
            </>
        )

        const timedateModals = (isConfirming: boolean, width: number) => {
            const style = { cursor: 'default' }
            if (width < 160) {
                return (
                    <div style={{ marginTop: isConfirming ? '32px' : '0px', ...style }}>
                        <S.MiniModalArrow></S.MiniModalArrow>
                        <S.SingleMiniModal>
                            <i style={{ paddingRight: '1px' }} className="fas fa-play-circle"></i>
                            {i18n(moment(selectedStartTime).format(dateFormat))}
                            <i style={{ paddingLeft: '10px', paddingRight: '1px' }} className="fas fa-play-circle"></i>
                            {i18n(moment(selectedEndTime).format(dateFormat))}
                        </S.SingleMiniModal>
                    </div>
                )
            } else {
                return (
                    <>
                        <div style={{ marginTop: '0px', float: 'right', ...style }}>
                            <S.MiniModalArrow></S.MiniModalArrow>
                            <S.MiniModal style={{ marginLeft: '0px', marginRight: '-40px' }}>
                                <i className="fas fa-play-circle"></i>
                                {i18n(moment(selectedEndTime).format(dateFormat))}
                            </S.MiniModal>
                        </div>
                        <div style={{ marginTop: isConfirming ? '32px' : '0px' }}>
                            <S.MiniModalArrow></S.MiniModalArrow>
                            <S.MiniModal>
                                <i className="fas fa-play-circle"></i>
                                {i18n(moment(selectedStartTime).format(dateFormat))}
                            </S.MiniModal>
                        </div>
                    </>
                )
            }
        }

        const relationConfirmIcons = (
            <div id={item.id} className="linkIcons">
                <i className="icon-not-selected far fa-circle"></i>
                <i className="icon-selected fas fa-circle"></i>
            </div>
        )

        const confirmationButtons = (
            <S.ConfirmationButtons
                style={{
                    float: confirmChanges === 'move' ? 'unset' : undefined,
                    marginLeft: confirmChanges === 'move' ? '20px' : undefined,
                    marginBottom: confirmChanges === 'move' ? '-30px' : undefined,
                    minWidth: '30px',
                }}
                isConfirm={confirmChanges}
            >
                <S.ConfirmationIcon
                    type={'confirm'}
                    className="fas fa-check-circle"
                    onClick={() => {
                        saveChanges(item)
                    }}
                ></S.ConfirmationIcon>
                <S.ConfirmationIcon
                    type={'cancel'}
                    className="fas fa-times-circle"
                    onClick={() => restoreItemDate()}
                ></S.ConfirmationIcon>
            </S.ConfirmationButtons>
        )

        const generateRelationsFromItem = (listPredecessor: string[], itemId: string, item: GanttItem) => {
            return listPredecessor.map((predecessorId, index) => {
                let predecessorItem = ganttItems.find(item => item.group === predecessorId)
                if (predecessorItem) {
                    if (predecessorItem.position < item.position) {
                        return generateRelations(
                            viewModeValue.value !== 'planned' ? itemId + '-realized' : itemId,
                            viewModeValue.value !== 'planned' ? predecessorId + '-realized' : predecessorId,
                            'require_end_to_start'
                        )
                    } else {
                        return generateRelations(
                            viewModeValue.value !== 'planned' ? itemId + '-realized' : itemId,
                            viewModeValue.value !== 'planned' ? predecessorId + '-realized' : predecessorId,
                            'require_end_to_start_reverse'
                        )
                    }
                }
            })
        }

        const generateRelationsFromReceiverItem = (listSucessor: string[], itemId: string) => {
            return listSucessor.map((sucessorId, index) => {
                const sucessorItem = ganttItems.find(item => item.group === sucessorId)
                if (sucessorItem) {
                    if (sucessorItem.position > item.position) {
                        return generateRelations(
                            viewModeValue.value !== 'planned' ? sucessorId + '-realized' : sucessorId,
                            viewModeValue.value !== 'planned' ? itemId + '-realized' : itemId,
                            'require_end_to_start'
                        )
                    } else {
                        return generateRelations(
                            viewModeValue.value !== 'planned' ? sucessorId + '-realized' : sucessorId,
                            viewModeValue.value !== 'planned' ? itemId + '-realized' : itemId,
                            'require_end_to_start_reverse'
                        )
                    }
                }
            })
        }
        const { left: leftResizeProps, right: rightResizeProps } = getResizeProps()

        return (
            <div
                id={item.id}
                {...getItemProps({
                    style,
                    // ref: getRef,
                    onMouseDown: () => {
                        console.log('on item clicked', item)
                        if (
                            !confirmChanges &&
                            !linkId &&
                            !showAddRelationModal &&
                            !mouseFollow &&
                            !(selectedItem && selectedItem.id !== item.id)
                        ) {
                            if (item.realized) {
                                setSelectedItem(item)
                                setSelectedStartTime(item.start_time)
                                setSelectedEndTime(item.end_time)
                            }
                            if (!editable) {
                                setSelectedNodeId && setSelectedNodeId(item.group as number)
                                setSelectedIndex && setSelectedIndex(item.group as number)
                            }
                        }
                    }
                })}
            >
                <S.ResizeArea {...leftResizeProps} left />
                {item.realized && (
                    <>
                        <S.ArrowAux
                            id={item.id + '-reverse'}
                            selected={itemContext.selected || !linkId}
                            style={{
                                marginTop: linkId && !confirmChanges ? '-36px' : '-5px',
                                marginBottom: linkId && !confirmChanges ? '0px' : undefined
                            }}
                        ></S.ArrowAux>
                        <S.ArrowAux
                            id={item.id + '-aux'}
                            selected={itemContext.selected || !linkId}
                            style={{
                                marginTop: linkId && !confirmChanges ? '18px' : undefined,
                                marginBottom: linkId && !confirmChanges ? '-18px' : undefined
                            }}
                        ></S.ArrowAux>
                        {item.predecessorsIdsList &&
                            !confirmChanges &&
                            (item.side === 'right' || !item.side) &&
                            generateRelationsFromItem(item.predecessorsIdsList, item.group, item)}
                        {item.successorIds &&
                            (item.side === 'left' || !item.side) &&
                            generateRelationsFromReceiverItem(item.successorIds, item.group)}
                    </>
                )}
                {item.realized && (
                    <>
                        {confirmChanges && selectedItem.id === item.id && <>{confirmationButtons}</>}
                        {showAddRelationModal && !confirmChanges && !linkId && itemContext.selected && (
                            <>
                                {direction !== 'right' && <S.SelectorModalLeft>{modalSelector}</S.SelectorModalLeft>}
                                {direction !== 'left' && <S.SelectorModalRight>{modalSelector}</S.SelectorModalRight>}
                            </>
                        )}

                        <div
                            style={{
                                width: '100%',
                                marginTop: !item.isLeaf || item.isDisabled ? '18px' : undefined
                            }}
                        >
                            {itemContext.selected && (
                                <>
                                    {(!confirmChanges || mouseFollow) &&
                                        item.isLeaf &&
                                        !item.isDisabled &&
                                        addLinkButtons}
                                    {!linkId && !showAddRelationModal && (
                                        <>{timedateModals(confirmChanges, itemContext.dimensions.width)}</>
                                    )}
                                </>
                            )}
                            {linkId &&
                                !confirmChanges &&
                                !parentList.includes(item.id) &&
                                linkId !== item.id &&
                                !checkExistingRelation() &&
                                item.isLeaf &&
                                !item.isDisabled && (
                                    <>
                                        {direction !== 'right' && (
                                            <S.SelectLinkButtonRight
                                                onClick={() => {
                                                    validadeClick('right')
                                                }}
                                            >
                                                {relationConfirmIcons}
                                            </S.SelectLinkButtonRight>
                                        )}

                                        {direction !== 'left' && (
                                            <S.SelectLinkButtonLeft
                                                onClick={() => {
                                                    validadeClick('left')
                                                }}
                                            >
                                                {relationConfirmIcons}
                                            </S.SelectLinkButtonLeft>
                                        )}
                                    </>
                                )}
                        </div>
                    </>
                )}

                <S.ResizeArea {...rightResizeProps} right />
            </div>
        )
    }

    const validateRelationCreationByGantt = (
        linkId: string | undefined,
        itemId: string,
        showModal: any,
        setDirection: any,
        direction: string,
        connectionType: string,
        item: GanttItem
    ) => {
        if (linkId && saveOperationChanges) {
            setMouseFollow(false)
            const parsedLinkIdArray = linkId.split('-')
            let parsedLinkId
            if (parsedLinkIdArray.length >= 3) {
                parsedLinkId = '-' + parsedLinkIdArray[1]
            } else {
                parsedLinkId = parsedLinkIdArray[0]
            }

            const datesPayload = item.executed
                ? {
                      executedStartDate: item.start_time as Moment,
                      executedEndDate: item.end_time as Moment
                  }
                : item.execution
                ? { executedStartDate: item.start_time as Moment }
                : {}

            if (timelineRef) {
                setVisibleStartTime(timelineRef.state.visibleTimeStart)
                setVisibleEndTime(timelineRef.state.visibleTimeEnd)
            }

            saveOperationChanges(
                {
                    ...datesPayload,
                    predecessorsIdsList: item.predecessorsIdsList ? [...item.predecessorsIdsList, itemId] : [itemId]
                },
                parsedLinkId
            )
        } else {
            showModal(true)
            setDirection(direction)
        }
    }

    const generateArrow = (
        start: any,
        end: any,
        startAnchor: string,
        endAnchor: string,
        offSet1 = 20,
        offSet2 = 20,
        showTail = false,
        showHead = true,
        gridBreak = 0,
        headSize = 10,
        strokeWidth = 2,
        color = 'black',
        zIndex = 1,
        path = 'grid'
    ) => {
        return (
            <Xarrow
                divContainerStyle={{ zIndex: zIndex }}
                SVGcanvasStyle={{ zIndex: zIndex }}
                headSize={headSize}
                strokeWidth={strokeWidth}
                gridBreak={gridBreak}
                _cpx1Offset={offSet1}
                _cpx2Offset={offSet2}
                color={color}
                startAnchor={startAnchor}
                endAnchor={endAnchor}
                showTail={showTail}
                showHead={showHead}
                path={path}
                start={start}
                end={end}
                headShape={arrowSvg}
                tailShape={arrowSvg}
            />
        )
    }
    const generateRelations = (start: string, end: string, type: string) => {
        if (type === 'end_together') {
            return (
                <>
                    {generateArrow(start, end, 'right', 'right')}
                    {generateArrow(end, start, 'right', 'right')}
                </>
            )
        } else if (type === 'require_end_to_end') {
            return <>{generateArrow(start, end, 'right', 'right', 10, 10, true, false, 1)}</>
        } else if (type === 'start_together') {
            return (
                <>
                    {generateArrow(start, end, 'left', 'left', -20, -20, true)}
                    {generateArrow(end, start, 'left', 'left', -20, -20, true)}
                </>
            )
        } else if (type === 'require_start_to_start') {
            return <>{generateArrow(start, end, 'left', 'left', -10, -10, true)}</>
        } else if (type === 'require_end_to_start') {
            return (
                <>
                    {generateArrow(end, end + '-aux', 'right', 'right', 15, 15, false, false, 0)}
                    {generateArrow(start, end + '-aux', 'left', 'right', -15, -15, true, false, 1)}
                </>
            )
        } else if (type === 'require_end_to_start_reverse') {
            return (
                <>
                    {generateArrow(end, end + '-reverse', 'right', 'right', 15, 15, false, false, 0)}
                    {generateArrow(start, end + '-reverse', 'left', 'right', -15, -15, true, false, 1)}
                </>
            )
        } else if (type === 'start_end_together') {
            return (
                <>
                    {generateArrow(start, end, 'left', 'left', -10, -10, false, false)}
                    {generateArrow(end, start, 'right', 'right', 10, 10, false, false, 1)}
                </>
            )
        } else if (type === 'require_start_to_end') {
            return (
                <>
                    {generateArrow(start, end + 'aux', 'left', 'right', -20, -20, true, false)}
                    {generateArrow(end, end + 'aux', 'right', 'right', 20, 20, true, false)}
                </>
            )
        }
    }

    const restoreItemDate = () => {
        let selectedItemIndex = ganttItems.findIndex(item => item.id === selectedItem.id)
        let newGanttItems = [...ganttItems]
        newGanttItems[selectedItemIndex].start_time = moment(originalStartTime)
        newGanttItems[selectedItemIndex].end_time = moment(originalEndTime)
        setGanttItems(newGanttItems)
        setSelectedStartTime(newGanttItems[selectedItemIndex].start_time)
        setSelectedEndTime(newGanttItems[selectedItemIndex].end_time)
        setConfirmChanges(undefined)
    }

    const saveChanges = item => {
        if (saveOperationChanges) {
            if (timelineRef) {
                setVisibleStartTime(timelineRef.state.visibleTimeStart)
                setVisibleEndTime(timelineRef.state.visibleTimeEnd)
            }
            saveOperationChanges(
                {
                    executedStartDate: moment(item.start_time),
                    executedEndDate: moment(item.end_time)
                },
                item.group
            )
        }
        setConfirmChanges(undefined)
    }

    const handleItemMove = (itemId, dragTime, newGroupOrder) => {
        if (editable && viewModeValue.value !== 'planned') {
            const group = groups[newGroupOrder]

            const newItems = ganttItems.map(item =>
                item.id === itemId
                    ? Object.assign({}, item, {
                          start_time: moment(dragTime),
                          end_time: moment(dragTime + (item.end_time - item.start_time)),
                          group: group.id
                      })
                    : item
            )

            setGanttItems(newItems)
            console.log('Moved', itemId, dragTime, newGroupOrder)
        }
    }

    const handleItemResize = (itemId, time, edge) => {
        if (editable && viewModeValue.value !== 'planned') {
            const newItems = ganttItems.map(item =>
                item.id === itemId
                    ? Object.assign({}, item, {
                          start_time: edge === 'left' ? moment(time) : item.start_time,
                          end_time: edge === 'left' ? item.end_time : moment(time)
                      })
                    : item
            )

            setGanttItems(newItems)
            console.log('Resized', itemId, time, edge)
        }
    }

    const handleDeselect = e => {
        setMouseFollow(false)
        setLinkId(undefined)
        setDirection(undefined)
        setShowAddRelationModal(false)
        setSelectedItem(undefined)
        restoreItemDate()
    }

    const ganttTimeValidator = (action, item, time, resizeEdge) => {
        if (selectedItem && !selectedItem.isLeaf) {
            return moment(resizeEdge === 'right' && action !== 'move' ? item.end_time : item.start_time)
        }
        if (action === 'move') {
            const newEndTime = item.end_time.valueOf() + (time - item.start_time.valueOf())
            if (item.lastEndDate && item.lastEndDate.valueOf() <= time) {
                setSelectedStartTime(moment(time))
                setSelectedEndTime(moment(newEndTime))
                if (!confirmChanges) {
                    setOriginalStartTime(item.start_time.valueOf())
                    setOriginalEndTime(item.end_time.valueOf())
                }
                setConfirmChanges('move')
                return moment(time)
            }
        } else {
            if (resizeEdge === 'left') {
                if (item.lastEndDate && item.lastEndDate.valueOf() <= time) {
                    setSelectedStartTime(moment(time))
                    if (!confirmChanges) {
                        setOriginalStartTime(item.start_time.valueOf())
                        setOriginalEndTime(item.end_time.valueOf())
                    }
                    setConfirmChanges('left')
                    return moment(time)
                }
            } else if (resizeEdge === 'right') {
                setSelectedEndTime(moment(time))
                if (!confirmChanges) {
                    setOriginalStartTime(item.start_time.valueOf())
                    setOriginalEndTime(item.end_time.valueOf())
                }
                setConfirmChanges('right')
                return moment(time)
            }
        }
        return moment(item.start_time)
    }

    const handleSelect = (id, e, time) => {
        let newSelectedItem = ganttItems.find(item => item.id === id)
        if (newSelectedItem && newSelectedItem.realized) {
            if (selectedItem && selectedItem.id !== id && !mouseFollow) {
                setLinkId(undefined)
                setDirection(undefined)
                setShowAddRelationModal(false)
                setSelectedItem(newSelectedItem)
            }
            if (selectedItem && confirmChanges) {
                restoreItemDate()
            }
            if (linkId === undefined) {
                setDirection(undefined)
                setSelectedItem(newSelectedItem)
                setSelectedStartTime(newSelectedItem.start_time)
                setSelectedEndTime(newSelectedItem.end_time)
            }
        }
    }

    const TimelineProps = {
        resizeDetector: containerResizeDetector,
        itemTouchSendsClick: false,
        groups: groups,
        items: ganttItems,
        onCanvasClick: (groupId: number) => {
            if (setSelectedNodeId && !editable) {
                setSelectedNodeId(groupId)
            }
            if (setSelectedIndex && !editable) {
                setSelectedIndex(groupId)
            }
        },
        rightSidebarWidth: 1,
        horizontalLineClassNamesForGroup: group => (group.derrick === 'Aux' ? ['row-aux'] : ['row-main']),
        moveResizeValidator: ganttTimeValidator,
        canChangeGroup: false,
        onItemMove: handleItemMove,
        onItemResize: handleItemResize,
        onItemDeselect: handleDeselect,
        lineHeight: lineHeight ? lineHeight : 25,
        sidebarWidth: 0,
        itemRenderer: itemRenderer,
        onItemSelect: handleSelect,
        dragSnap: 60 * 1000
    }

    const generateVerticalDividers = width => {
        let amountDividers = 0
        if (divisorsUnit === 'hour') {
            amountDividers = 6
        } else if (divisorsUnit === 'day') {
            amountDividers = 24
        } else if (divisorsUnit === 'day/simple') {
            amountDividers = 2
        } else if (divisorsUnit === 'week') {
            amountDividers = 7
        } else if (divisorsUnit === 'month') {
            amountDividers = 30
        } else if (divisorsUnit === 'year') {
            amountDividers = 5
        }

        let dividerString = (width / amountDividers).toString()
        return (
            <div style={{ display: 'flex' }}>
                {[...Array(amountDividers)].map((value: undefined, index: number) => (
                    <div
                        style={{
                            width: dividerString + 'px',
                            borderLeft: 'gray solid 1px',
                            // marginRight: '1px',
                            marginBottom: '-2px',
                            height: index === 0 || index === amountDividers ? '15px' : '10px',
                            marginTop: index === 0 || index === amountDividers ? '-5px' : '0px',
                            //background: 'white',
                            zIndex: 1000,
                            position: 'relative'
                        }}
                    />
                ))}
            </div>
        )
    }

    const TimelineHeader = (
        <TimelineHeaders style={{ position: 'sticky', top: hideHeader ? '0px' : '33px', zIndex: 89 }}>
            <DateHeader
                style={{
                    maxHeight: '27px',
                    background: '#DDDEE1',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: 'black'
                }}
                height={27}
                unit={parentUnit(unit)}
                labelFormat={FORMAT_STRINGS_PARENT[parentUnit(unit)]}
            />
            {!hideHeader && (
                <DateHeader
                    style={{
                        maxHeight: '27px',
                        background: '#DDDEE1',
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#606A80'
                    }}
                    height={hideHeader ? 29 : 27}
                    unit={unit}
                    labelFormat={FORMAT_STRINGS[unit]}
                    intervalRenderer={({ getIntervalProps, intervalContext, data }) => {
                        if (
                            intervalContext.interval.labelWidth.toFixed(2) != labelWidth &&
                            divisorsUnit !== 'month' &&
                            divisorsUnit !== 'year'
                        ) {
                            setLabelWidth(intervalContext.interval.labelWidth.toFixed(2))
                        }
                        return (
                            <div {...getIntervalProps()}>
                                <div style={{ textAlign: 'center' }}>{intervalContext.intervalText}</div>
                                {divisorsUnit !== 'month' && divisorsUnit !== 'year'
                                    ? divisorComponent
                                    : generateVerticalDividers(intervalContext.interval.labelWidth)}
                            </div>
                        )
                    }}
                />
            )}
        </TimelineHeaders>
    )

    return (
        <S.GanttWrapper>
            {!hideHeader && (
                <>
                    <S.MouseGanttLink>
                        <MouseTooltip
                            onDrag={forceRerender}
                            visible={mouseFollow}
                            offsetX={-1}
                            offsetY={-1}
                            buttonRef={linkButtonRef}
                            updatePosition={updateXYPlusButton}
                        >
                            <div id="mouseTooltip"></div>
                        </MouseTooltip>
                        {mouseFollow && linkId && direction && linkButtonState && (
                            <Xarrow
                                start="mouseTooltip"
                                end={linkButtonState.id}
                                endAnchor={'middle'}
                                showHead={false}
                                strokeWidth={2}
                                curveness={0}
                            />
                        )}
                        {mouseFollow && linkId && direction && !linkButtonState && (
                            <Xarrow
                                start="mouseTooltip"
                                end={'positionLinkTeste'}
                                endAnchor={'middle'}
                                showHead={false}
                                strokeWidth={2}
                                curveness={0}
                            />
                        )}
                    </S.MouseGanttLink>
                    <S.GanttHeader style={{ position: 'sticky', top: 0, zIndex: 90 }}>
                        <S.EditGanttSection>
                            <ActionToggle
                                onChange={() => {
                                    setEditable(!editable)
                                }}
                                active={editable}
                            />
                            <span>{i18n('Edit Gantt')}</span>
                        </S.EditGanttSection>
                        <LiveSelect
                            style={{ height: '20px' }}
                            value={viewModeValueIcon}
                            options={viewModeOptions}
                            placeholder={<i className="fas fa-eye"></i>}
                            clearable={false}
                            searchable={false}
                            onChange={(value, option) => {
                                if (value && option) {
                                    viewModeOnChange(value, option)
                                }
                            }}
                        />
                        <S.ZoomIcons>
                            <button className={'plus'} onClick={() => changeZoomLevel('plus')}>
                                <i className="fal fa-search-plus" />
                            </button>
                            <LiveSelect
                                value={{
                                    label: zoomLevelsLabels[zoom],
                                    value: zoom
                                }}
                                options={zoomLevelsLabels.map((zoomLabel, index) => {
                                    return {
                                        label: zoomLabel,
                                        value: index
                                    }
                                })}
                                clearable={false}
                                searchable={false}
                                onChange={(value, option) => {
                                    if (option) {
                                        let newZoomRange = setZoomRange(zoomLevels[option.value])
                                        if (setZoom) {
                                            setZoom(option.value)
                                        }
                                        if (setVisibleStartTime) {
                                            setVisibleStartTime(newZoomRange.visibleTimeStart)
                                        }
                                        if (setVisibleEndTime) {
                                            setVisibleEndTime(newZoomRange.visibleTimeEnd)
                                        }
                                        timelineRef.updateScrollCanvas(
                                            newZoomRange.visibleTimeStart,
                                            newZoomRange.visibleTimeEnd
                                        )
                                    }
                                }}
                            />
                            <button className={'minus'} onClick={() => changeZoomLevel('minus')}>
                                <i className="fal fa-search-minus" />
                            </button>
                        </S.ZoomIcons>
                    </S.GanttHeader>
                </>
            )}
            <S.TimelineWrapper ref={scrollRef}>
                <Timeline
                    {...TimelineProps}
                    fullUpdate
                    ref={(r: any) => setTimelineRef(r)}
                    {...(!editable || !selectedItem || viewModeValue.value === 'planned'
                        ? { selected: [] }
                        : { selected: [selectedItem.id] })}
                    defaultTimeStart={moment(visibleStartTime.valueOf())}
                    defaultTimeEnd={moment(visibleEndTime.valueOf())}
                    canMove={viewModeValue.value !== 'planned' && editable}
                    canResize={viewModeValue.value !== 'planned' && editable ? 'both' : false}
                    useResizeHandle
                >
                    {TimelineHeader}
                </Timeline>
            </S.TimelineWrapper>
        </S.GanttWrapper>
    )
}

export default GanttChart
